import { Component, DestroyRef, DOCUMENT, inject, Renderer2 } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, finalize, take } from 'rxjs/operators';
import { SessionStorageService } from './services/session-storage.service';
import { ShopService } from './services/shop.service';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateService } from '@ngx-translate/core';
import { LanguageConsts, LanguageItem } from './consts/languages';
import { Pages } from './consts/pages';
import { AccessibilityService } from './services/accessibility.service';
import { ErrorService } from './services/error.service';
import { UrlParam } from './models/url-param';
import { LoginResponse } from './dtos/login.response.dto';
import { AuthToken } from './models/auth-token';
import { InitDataResponse } from './dtos/init-data.response.dto';
import { AppInitService } from './services/app-init.service';

@Component({
    standalone: false,
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent {
    private destroyRef = inject(DestroyRef);
    private shopService = inject(ShopService);
    private sessionStorageService = inject(SessionStorageService);
    private router = inject(Router);
    private translateService = inject(TranslateService);
    private route = inject(ActivatedRoute);
    private errorService = inject(ErrorService);
    private accessibilityService = inject(AccessibilityService);
    private renderer = inject(Renderer2);
    private document = inject(DOCUMENT);
    private appInitService = inject(AppInitService);

    title = 'webshop';
    errorMessages: string[];

    constructor() {
        this.accessibilityService.accessibilityMode$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: isEnabled => {
                    if (isEnabled) {
                        this.renderer.addClass(document.body, 'accessibility-mode');
                    } else {
                        this.renderer.removeClass(document.body, 'accessibility-mode');
                    }
                }
            })

        // A query paramokat csak EGYSZER, a kezdeti navig�ci� befejez�d�se ut�n �rt�kelj�k ki,
        // �gy nem sz�l bele a bootstrap alatti �res emisszi�, sem a k�s�bbi navigateByUrl.
        if (this.router.navigated) {
            this.handleStartup(this.route.snapshot.queryParams as UrlParam);
        } else {
            this.router.events
                .pipe(
                    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                    take(1),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(() => this.handleStartup(this.route.snapshot.queryParams as UrlParam));
        }
    }

    findLanguageByTwoDigitCode(languageTwoDigitCode: string): LanguageItem {
        for (const [key, value] of Object.entries(LanguageConsts)) {
            if (value.twoDigit === languageTwoDigitCode)
                return value;
        }

        return null;
    }

    checkUrlParamIsOk(param: UrlParam): boolean {
        let hasError: boolean = false;
        this.errorMessages = [];

        if (!param.HotelId) {
            hasError = true;
            this.errorMessages.push("Missing querystring parameter: 'HotelId'");
        } else {
            if (isNaN(param.HotelId)) {
                hasError = true;
                this.errorMessages.push("The HotelId can by only number type: " + param.HotelId);
            }
        }

        if (!param.Language) {
            hasError = true;
            this.errorMessages.push("Missing querystring parameter: 'Language'");
        }
        else {
            if (!this.findLanguageByTwoDigitCode(param.Language)) {
                hasError = true;
                this.errorMessages.push("Invalid language code: " + param.Language);
            }
        }

        if (!param.Subscriber) {
            hasError = true;
            this.errorMessages.push("Missing querystring parameter: 'Subscriber'");
        }

        return !hasError;
    }

    private handleStartup(params: UrlParam): void {
        let checkQueryStringsParameters: boolean = true;

        const currentPath = this.router.url.split('?')[0];

        // paymentcallbackn�l nem kell a querystring vizsg�lat
        if (currentPath === "/paymentcallback")
            checkQueryStringsParameters = false;

        const hasQueryParams = params && Object.keys(params).length > 0;

        if (checkQueryStringsParameters && hasQueryParams) {
            let paramTemp: UrlParam = params as UrlParam;
            if (this.checkUrlParamIsOk(paramTemp)) {

                let param = new UrlParam();
                param.HotelId = paramTemp.HotelId;
                param.Language = paramTemp.Language;
                param.Subscriber = paramTemp.Subscriber;

                param.Language4Digit = this.findLanguageByTwoDigitCode(param.Language).fiveDigit;

                this.shopService.login(param)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: (tokenData) => {
                            const result = tokenData as LoginResponse;

                            this.sessionStorageService.saveUrlParams(param);

                            this.translateService.use(param.Language)
                                .pipe(takeUntilDestroyed(this.destroyRef))
                                .subscribe({
                                    next: () => {
                                        let date: Date = new Date();
                                        date.setSeconds(result.expires_in);

                                        const token: AuthToken = { AccessToken: result.access_token, ExpiresIn: date };
                                        this.sessionStorageService.setAuthToken(token);

                                        // token + url param k�szen �ll -> runAlways() v�g�n jelezz�k a page-eknek
                                        this.runGetInitData();

                                        // TODO: refund eset�n ne ugorjon m�r �t a landingra, de ez egy cs�nya megold�s
                                        if (currentPath !== "/refund")
                                            this.router.navigateByUrl(Pages.landing)
                                    },
                                    error: () => {
                                        console.error(`Problem with '${param.Language}' language initialization.'`);
                                        // hiba eset�n is engedj�k tov�bb a page-eket (k�l�nben �r�kre v�rn�nak)
                                        this.appInitService.markReady();
                                    }
                                })
                        }, error: (err) => {
                            this.appInitService.markReady();
                            let dialogRef = this.errorService.showError(err);
                            //dialogRef.afterClosed().subscribe(result => {
                            //    if (param.ReturnUrl)
                            //        HwUtil.redirectWithoutHistory(param.ReturnUrl);
                            //});
                        }
                    })
            }
            else {
                this.appInitService.markReady();
                let dialogRef = this.errorService.showErrors(this.errorMessages);
                //dialogRef.afterClosed().subscribe(result => {
                //    if (paramTemp.ReturnUrl)
                //        HwUtil.redirectWithoutHistory(paramTemp.ReturnUrl);
                //});
            }
        }
        else {
            this.runGetInitData();
        }
    }

    private runGetInitData(): void {
        this.shopService.getInitData()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    // Az app inicializ�l�s k�sz -> a page-ek ind�thatj�k a saj�t adatlek�r�seiket
                    this.appInitService.markReady();
                })
            )
            .subscribe({
                next: (result) => {
                    const initData = result as InitDataResponse;

                    this.appInitService.initData = initData;

                    const styleElement = this.renderer.createElement('style');
                    this.renderer.setProperty(styleElement, 'textContent', initData.customCss);
                    this.renderer.appendChild(this.document.head, styleElement);
                },
                error: (err) => this.errorService.showError(err)
            });
    }
}
